import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import {useAppSelector} from "../../../../redux/store";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import Link from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import ConstCenterVerfication from "../CostCenterVerfication/CostCenterVerification";
import IndividualPaymentLimitsReportDialog from "components/Dialogs/IndividualPaymentLimitsReportDialog";
import {dashBoardMiscstyle} from "./dashBoardMiscstyle";
import {
  handleAxiosErrors,
  initialMessage,
  loggedMessage,
  message,
} from "commonUtils/utils";
import {Alerts, CPSConstants, PageNames} from "commonUtils/enumerations";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {logMessage} from "services/loggerService";
import "../../../Dashboard/Dashboard.css";
import {encrypt} from "commonUtils/crypto";

const DashboardMiscellaneous: React.FC<any> = miscData => {
  const {authDetails, userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const [reportopen, setReportOpen] = React.useState<boolean>(false);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);

  const handleOpenReportDialog = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has opened the Individual Payment Limits Report dialog.`,
        PageNames.DASHBOARD,
      );
      setReportOpen(true);
    } catch (error: any) {
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  };

  const handleClickOrf = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has clicked the 'Create Ownership Requests' link.`,
        PageNames.LOGIN,
      );
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  };

  return (
    <Paper className="paper-look">
      {reportopen && (
        <IndividualPaymentLimitsReportDialog
          reportopen={reportopen}
          setOpen={setReportOpen}
          buttononename="Export to Excel"
          buttontwoname="Close"
        />
      )}
      {showAlert && serverValidation?.message && (
        <FormValidationAlert
          closeIcon={true}
          showAlertBox={showAlert}
          setShowAlertBox={setShowAlert}
          severity={
            (serverValidation?.isSuccessMessage ?? false)
              ? Alerts.SUCCESS
              : (serverValidation?.isValidationMessage ?? false)
                ? Alerts.INFO
                : Alerts.ERROR
          }
          message={serverValidation?.message}
        />
      )}
      <Typography component="h1" className="alert-heading">
        <MiscellaneousServicesOutlinedIcon className="outline-icon" />
        Miscellaneous
      </Typography>
      <Box className="my-border" />
      <Box className="box-padding-bottom" />
      <List className="miscellaneous-my-list miscellaneous-alert-block">
        {miscData?.miscData?.permissionToViewPaymentLimitsReport && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography component="span" className="alert-count">
                      Report
                    </Typography>
                  </>
                }
                secondary={
                  <>
                    <Link
                      sx={dashBoardMiscstyle.cusrsorPointer}
                      onClick={handleOpenReportDialog}>
                      <Typography
                        component="span"
                        className="individual-payment-limit">
                        Individual Payment Limits Report
                      </Typography>
                    </Link>
                  </>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}

        {miscData?.miscData?.permissionToViewARF && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography
                      component="h1"
                      className="advising-request-form">
                      Advising Request Form
                    </Typography>
                  </>
                }
                secondary={
                  <>
                    <Link
                      sx={dashBoardMiscstyle.cusrsorPointer}
                      onClick={() => {
                        window.open(
                          `${window.location.origin}/activity/arfform?param=` +
                            encrypt("IsFromDashboard=true"),
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                      target="_blank">
                      <Typography
                        component="span"
                        className="individual-payment-limit">
                        Click Here to Create ARF Form
                      </Typography>
                    </Link>
                  </>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}

        {miscData?.miscData?.permissionToViewORF && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography
                      component="span"
                      className="advising-request-form">
                      Advisor/Speaker Alignment Requests
                    </Typography>
                  </>
                }
                secondary={
                  <React.Fragment>
                    <Stack spacing={1}>
                      <Link
                        sx={dashBoardMiscstyle.cusrsorPointer}
                        onClick={() => {
                          handleClickOrf();
                          window.open(
                            `${window.location.origin}/${miscData?.miscData?.orfUrl}`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                        target="_blank">
                        <Typography
                          component="span"
                          className="individual-payment-limit"
                          display="block">
                          Create Alignment Requests
                        </Typography>
                      </Link>
                      <Link
                        href={`${CPSConstants.APPENDVIEWOWNERSHIP}`}
                        onClick={handleClickOrf}>
                        <Typography
                          component="span"
                          className="individual-payment-limit"
                          display="block">
                          View Alignment Requests
                        </Typography>
                      </Link>
                    </Stack>
                  </React.Fragment>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}

        {miscData?.miscData?.permissionToViewExpenseApprovalRequests && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography
                    component="h1"
                    className="advising-request-form"
                  >
                    Expense Approval Requests
                  </Typography>
                }
                secondary={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      mt: 0.5,
                    }}
                  >
                    <Link
                      href={
                        miscData?.miscData?.expenseApprovalRequestUrl ??
                        CPSConstants.APPENDEXPENSEAPPROVALREQUESTS_URL
                      }
                      underline="always"
                    >
                      <Typography
                        component="span"
                        className="individual-payment-limit"
                      >
                        View Expense Approval Requests
                      </Typography>
                    </Link>

                    <Typography
                      component="h1"
                      className="advising-request-form"
                    >
                      {miscData?.miscData?.expenseApprovalRequestCount ?? 0}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}
        <ListItem alignItems="flex-start">
          <ConstCenterVerfication />
        </ListItem>
        <Divider />
      </List>
    </Paper>
  );
};
export default DashboardMiscellaneous;
